import React from 'react';
import { SendToBack, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PartLookupInput from '../ui/part-lookup-input';
import ZeroValueBadge from './ZeroValueBadge';

const RemoveRefitButton = ({
  part,
  zoneId,
  onUpdatePart,
  childAccessParts = [],
  onAddAccessPart,
  onRemoveAccessPart,
  onUpdateAccessPart,
  insurerConfigId,
  defaultsReady = false,
}) => {
  const isActive = part.removeRefit;
  // Effective R&R hours: manual override wins, else the insurer line's default.
  const effectiveHours = part.removeRefitHoursOverride ?? part.defaultRemoveRefitHours;
  const needsAttention = defaultsReady && isActive && !(Number(effectiveHours) > 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`relative h-8 gap-1.5 text-xs ${
            isActive
              ? 'bg-chart-2 border-chart-2 text-white hover:bg-chart-2/90 hover:text-white'
              : 'text-muted-foreground border-border hover:text-foreground'
          }`}
        >
          {needsAttention && <ZeroValueBadge title="No R&R hours set — this line will be blank on the quote" />}
          <SendToBack className="h-3.5 w-3.5" />
          R&R
          {isActive && childAccessParts.length > 0 && (
            <span className="ml-0.5 text-[10px] bg-white/25 text-white rounded-full px-1.5">
              +{childAccessParts.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px]" align="start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`rr-pop-${part.id}`}
              checked={isActive}
              onCheckedChange={(checked) => onUpdatePart(zoneId, part.id, 'removeRefit', checked)}
            />
            <Label htmlFor={`rr-pop-${part.id}`} className="text-xs cursor-pointer font-medium">
              Remove & Refit
            </Label>
          </div>

          {isActive && (
            <div className="space-y-2 pt-1 border-t">
              <div className="space-y-1">
                <Label htmlFor={`rrHours-${part.id}`} className="text-xs text-muted-foreground">
                  R&R Hours
                </Label>
                <Input
                  id={`rrHours-${part.id}`}
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder={part.defaultRemoveRefitHours != null ? String(part.defaultRemoveRefitHours) : '0'}
                  value={part.removeRefitHoursOverride ?? ''}
                  onChange={(e) => onUpdatePart(zoneId, part.id, 'removeRefitHoursOverride', e.target.value ? Number(e.target.value) : null)}
                  className="h-7 text-xs"
                />
              </div>

              <div className="text-xs font-medium text-muted-foreground flex items-center justify-between">
                <span>{childAccessParts.length} part{childAccessParts.length !== 1 ? 's' : ''} for access</span>
                {childAccessParts.length > 0 && <span className="text-[10px]">Hours</span>}
              </div>
              {childAccessParts.length > 0 && (
                <div className="space-y-1.5">
                  {childAccessParts.map(accessPart => (
                    <div key={accessPart.id} className="flex items-center gap-1.5">
                      <PartLookupInput
                        value={accessPart.part}
                        onChange={(partName) => onUpdateAccessPart(zoneId, accessPart.id, 'part', partName)}
                        insurerConfigId={insurerConfigId}
                        zone={zoneId}
                        placeholder="Part name"
                        disabled={!insurerConfigId}
                        className="mb-0 flex-1"
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder={accessPart.defaultRemoveRefitHours != null ? String(accessPart.defaultRemoveRefitHours) : '0'}
                        value={accessPart.removeRefitHoursOverride ?? ''}
                        onChange={(e) => onUpdateAccessPart(zoneId, accessPart.id, 'removeRefitHoursOverride', e.target.value ? Number(e.target.value) : null)}
                        className="h-7 w-16 text-xs shrink-0"
                        title="R&R Hours"
                      />
                      <button
                        type="button"
                        onClick={() => onRemoveAccessPart(zoneId, accessPart.id)}
                        className="p-1 text-muted-foreground hover:text-destructive flex-shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <PartLookupInput
                value=""
                onChange={(partName) => {
                  if (partName && partName.trim()) {
                    onAddAccessPart(zoneId, part, partName);
                  }
                }}
                insurerConfigId={insurerConfigId}
                zone={zoneId}
                placeholder="Add access part..."
                disabled={!insurerConfigId}
                commitOnBlur={false}
                clearOnChange={true}
                className="mb-0"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RemoveRefitButton;
